// =====================================================================
// attach-context.ts (WP3 Task 8, site-scoped by Task 12) — one-shot handoff
// for the business info the owner already confirmed during WP2's
// demo-attach flow, so the wizard's Foundation step can pre-fill the
// canonical NAP fields instead of asking again. Same "stash on write,
// read-and-clear on take" shape as arrival-context.ts's DRAFT_STASH_KEY,
// deliberately not shared with it — this stash carries a different payload
// shape (a business object, not an id) and has a different sole consumer
// (FoundationStep, not a banner).
//
// Task 12 is the only caller of `stashAttachedBusiness` — it wires WP2's
// attach response (`{ locationId, business: PublicBusiness }`) into this stash
// on arrival, right before navigating to the wizard for THAT site. Task 8
// (foundation-step.tsx) is the only caller of `takeAttachedBusiness` +
// `deriveBusinessInfoSeed`.
//
// SITE-SCOPED (Task 12, carrying Task 8's own adversarial-review finding):
// the stash is written as `{ locationId, business }`, not a bare business, and
// `takeAttachedBusiness` only returns the business when the caller's
// `locationId` matches the one it was stashed for. Without this, an owner who
// attaches a business for site A, abandons the wizard before Foundation
// takes the stash, then later starts onboarding a DIFFERENT site B in the
// same tab would have site B seeded with site A's business. A mismatch is
// stale garbage either way — the stash is cleared on take REGARDLESS of
// whether the locationId matches, exactly like a corrupt-JSON take clears the
// key even though it returns null.
//
// Internal sessionStorage key: 'pb.attachBusiness'.
// =====================================================================

const ATTACH_STASH_KEY = 'pb.attachBusiness';

/**
 * The subset of WP2's `PublicBusiness` (apps/dashboard/src/lib/
 * draft-public.ts) this module cares about. Deliberately re-declared here
 * rather than imported — this is a web/src leaf, WP2's schema lives in the
 * worker's src/ tree, and only `name`/`category`/`city`/`region` ever feed
 * `deriveBusinessInfoSeed`. `rating`/`ratingCount`/`hoursSummary` may be
 * present on the real object but are never read here, so they're not
 * declared — an unknown extra key survives a JSON round-trip untouched.
 */
export interface PublicBusinessLike {
  name: string;
  category?: string;
  city?: string;
  region?: string;
}

/** The four canonical fields a seed PATCH can fill. Matches the exact
 *  mapping this task's brief specifies: businessName←name, addressCity←city,
 *  addressRegion←region, primaryCategory←category. */
export interface BusinessInfoFields {
  businessName: string;
  addressCity: string;
  addressRegion: string;
  primaryCategory: string;
}

/** The subset of GET `/api/sites/:id/business-info`'s response this module
 *  reads — every canonical field is nullable on the wire (see
 *  business-info-panel.tsx's own `BusinessInfo` interface, the mirrored
 *  original). Partial here additionally tolerates a caller that only has
 *  some of the four fields in hand (this task's own brief test case passes
 *  an object missing two of them). */
export type ExistingBusinessInfoFields = Partial<{
  businessName: string | null;
  addressCity: string | null;
  addressRegion: string | null;
  primaryCategory: string | null;
}>;

/** The on-disk shape written to sessionStorage — the business plus the id
 *  of the site it was attached for, so a mismatched `take` can be detected. */
interface AttachStash {
  locationId: string;
  business: PublicBusinessLike;
}

function looksLikeStash(value: unknown): value is AttachStash {
  if (value === null || typeof value !== 'object') return false;
  const v = value as { locationId?: unknown; business?: unknown };
  if (typeof v.locationId !== 'string') return false;
  if (v.business === null || typeof v.business !== 'object') return false;
  return typeof (v.business as { name?: unknown }).name === 'string';
}

/**
 * Stash a business to seed the Foundation step's business-info form with,
 * once — scoped to the site it was attached for. Overwrites any prior stash
 * (there is only ever one pending arrival at a time). Silently no-ops if
 * sessionStorage is unavailable (private mode / disabled) — non-fatal, the
 * owner just re-enters the fields by hand.
 */
export function stashAttachedBusiness(locationId: string, business: PublicBusinessLike): void {
  try {
    sessionStorage.setItem(ATTACH_STASH_KEY, JSON.stringify({ locationId, business }));
  } catch {
    // non-fatal — see doc comment above
  }
}

/**
 * Read-and-clear: returns the stashed business (if any) and removes it from
 * sessionStorage in the same call, so a second `take` — including one from a
 * StrictMode double-mount of the same effect — always sees nothing left to
 * seed. The stash is cleared REGARDLESS of outcome (corrupt JSON, a value
 * that doesn't look like a stash, or a `locationId` that doesn't match the
 * caller's) — a poisoned or stale entry can't keep failing on every future
 * read, and a mismatched stash is stale garbage the moment a second site's
 * wizard asks for it.
 */
export function takeAttachedBusiness(locationId: string): PublicBusinessLike | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(ATTACH_STASH_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  try {
    sessionStorage.removeItem(ATTACH_STASH_KEY);
  } catch {
    // non-fatal best-effort clear — worst case a private-mode failure here
    // leaves the raw value in place, but we still parse and return it below
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!looksLikeStash(parsed)) return null;
    if (parsed.locationId !== locationId) return null; // stale — a different site's stash
    return parsed.business;
  } catch {
    return null;
  }
}

function isEmptyField(v: string | null | undefined): boolean {
  return v === null || v === undefined || v === '';
}

/**
 * Pure. Builds a PATCH body for `/api/sites/:id/business-info` that fills
 * ONLY canonical fields that are currently empty (null, undefined, or '' —
 * all three count as "empty"), from the corresponding field on the attached
 * business. A field that is already set on `existing` is NEVER overwritten,
 * even if the attached business disagrees. Returns null when there is
 * nothing to seed (either every canonical field is already set, or the
 * attached business has nothing for the fields that are still empty).
 */
export function deriveBusinessInfoSeed(
  existing: ExistingBusinessInfoFields,
  business: PublicBusinessLike,
): Partial<BusinessInfoFields> | null {
  const seed: Partial<BusinessInfoFields> = {};

  if (isEmptyField(existing.businessName) && business.name) {
    seed.businessName = business.name;
  }
  if (isEmptyField(existing.addressCity) && business.city) {
    seed.addressCity = business.city;
  }
  if (isEmptyField(existing.addressRegion) && business.region) {
    seed.addressRegion = business.region;
  }
  if (isEmptyField(existing.primaryCategory) && business.category) {
    seed.primaryCategory = business.category;
  }

  return Object.keys(seed).length > 0 ? seed : null;
}
